import React, { useEffect, useRef, useState } from 'react';
import { motion, useDragControls, useMotionValue, useReducedMotion } from 'framer-motion';
import DesktopGlyph from './DesktopGlyph';
import UiIcon from '../ui/UiIcon';

const DesktopWindow = ({ title, icon, mark, iconSrc, active, visible, onActivate, onMinimize, onClose, children }) => {
  const boundsRef = useRef(null);
  const frameRef = useRef(null);
  const controls = useDragControls();
  const reduceMotion = useReducedMotion();
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const [maximized, setMaximized] = useState(false);
  const [canDrag, setCanDrag] = useState(false);

  useEffect(() => {
    const query = window.matchMedia('(min-width: 761px)');
    const resize = () => { setCanDrag(query.matches); x.set(0); y.set(0); };
    resize();
    window.addEventListener('resize', resize);
    return () => window.removeEventListener('resize', resize);
  }, [x, y]);

  useEffect(() => {
    if (active && visible && !frameRef.current?.contains(document.activeElement)) frameRef.current?.focus({ preventScroll: true });
  }, [active, visible]);

  const toggleMaximized = () => { x.set(0); y.set(0); setMaximized((current) => !current); };

  return <div ref={boundsRef} className={`desktop-window-anchor ${active ? 'is-active' : ''} ${maximized ? 'is-maximized' : ''}`} hidden={!visible}>
    <motion.section
      ref={frameRef}
      className={`desktop-app-window desktop-app-window--${icon}`}
      role="dialog"
      aria-label={title}
      tabIndex={-1}
      onPointerDownCapture={onActivate}
      onFocusCapture={onActivate}
      initial={reduceMotion ? false : { opacity: 0, scale: .98 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: reduceMotion ? 0 : .2 }}
      style={{ x, y }}
      drag={canDrag && !maximized}
      dragListener={false}
      dragControls={controls}
      dragConstraints={boundsRef}
      dragElastic={0}
      dragMomentum={false}
    >
      <header className="app-titlebar" onPointerDown={(event) => { if (canDrag && !maximized && !event.target.closest('button')) controls.start(event); }} onDoubleClick={(event) => { if (!event.target.closest('button')) toggleMaximized(); }}>
        <span><DesktopGlyph type={icon} mark={mark} src={iconSrc} />{title}</span>
        <div className="app-window-controls">
          <button type="button" onClick={onMinimize} aria-label={`Minimize ${title}`} title="Minimize"><UiIcon name="minimize" /></button>
          <button type="button" onClick={toggleMaximized} aria-label={`${maximized ? 'Restore' : 'Maximize'} ${title}`} title={maximized ? 'Restore' : 'Maximize'}><UiIcon name={maximized ? 'restore' : 'maximize'} /></button>
          <button type="button" onClick={onClose} aria-label={`Close ${title}`} title="Close"><UiIcon name="close" /></button>
        </div>
      </header>
      <div className="desktop-app-body">{children}</div>
    </motion.section>
  </div>;
};

export default DesktopWindow;
